import React, { useEffect, useState, useRef } from 'react';
import { RefreshCwIcon, ZoomInIcon, ZoomOutIcon } from 'lucide-react';
// Explicitly import Three.js to ensure it's available
let THREE;
try {
  THREE = require('three');
} catch (error) {
  console.error('Failed to load Three.js:', error);
}
// Renamed from Globe to GlobeVisualization to avoid naming conflicts
export function GlobeVisualization({
  locations,
  onSelectLocation,
  selectedLocationId
}) {
  const containerRef = useRef(null);
  const rendererRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const globeRef = useRef(null);
  const markersRef = useRef([]);
  const animationFrameIdRef = useRef(null);
  const isDraggingRef = useRef(false);
  const previousMousePositionRef = useRef({
    x: 0,
    y: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [threeJsError, setThreeJsError] = useState(false);
  // Constants for globe setup
  const GLOBE_RADIUS = 3;
  const MIN_ZOOM = 4;
  const MAX_ZOOM = 12;
  const DEFAULT_ZOOM = 7;
  // Function to calculate optimal camera position based on container size
  const calculateOptimalCameraPosition = (width, height) => {
    if (!width || !height) return DEFAULT_ZOOM;
    // Calculate the aspect ratio
    const aspect = width / height;
    // Adjust zoom based on aspect ratio and container size
    // This ensures the globe is fully visible regardless of container dimensions
    let optimalZoom = DEFAULT_ZOOM;
    // For narrower/taller containers, move camera back
    if (aspect < 1) {
      optimalZoom = DEFAULT_ZOOM + 1;
    }
    // For smaller containers overall, ensure globe fits
    const minDimension = Math.min(width, height);
    if (minDimension < 400) {
      optimalZoom = DEFAULT_ZOOM + 1.5;
    }
    return optimalZoom;
  };
  // Function to add location markers
  const addLocationMarkers = (globeRadius = 3) => {
    if (!THREE || !sceneRef.current || !globeRef.current) return;
    // Clear existing markers
    markersRef.current.forEach(marker => {
      if (marker.parent) {
        marker.parent.remove(marker);
      }
    });
    markersRef.current = [];
    // Create a group for all markers that will be attached to the globe
    const markersGroup = new THREE.Group();
    globeRef.current.add(markersGroup);
    // Add new markers
    locations.forEach(location => {
      const {
        lat,
        lng,
        id
      } = location;
      // Convert lat/lng to 3D coordinates on sphere
      const phi = (90 - lat) * (Math.PI / 180);
      const theta = (lng + 180) * (Math.PI / 180);
      // Calculate position on the globe surface
      const x = -(globeRadius * Math.sin(phi) * Math.cos(theta));
      const y = globeRadius * Math.cos(phi);
      const z = globeRadius * Math.sin(phi) * Math.sin(theta);
      // Create more elegant marker - a flatter disc with a glow effect
      const markerGeometry = new THREE.CircleGeometry(0.1, 32);
      // Create the main marker with Spotify green
      const markerMaterial = new THREE.MeshBasicMaterial({
        color: 0x1db954,
        transparent: true,
        opacity: 0.9
      });
      const marker = new THREE.Mesh(markerGeometry, markerMaterial);
      // Add a subtle glow halo around the marker
      const glowGeometry = new THREE.CircleGeometry(0.15, 32);
      const glowMaterial = new THREE.MeshBasicMaterial({
        color: 0x1db954,
        transparent: true,
        opacity: 0.3
      });
      const glow = new THREE.Mesh(glowGeometry, glowMaterial);
      // Group the marker and its glow
      const markerGroup = new THREE.Group();
      markerGroup.add(marker);
      markerGroup.add(glow);
      // Position marker slightly above the globe surface
      const position = new THREE.Vector3(x, y, z);
      const normal = position.clone().normalize();
      // Align the flat circle to face outward from the globe
      markerGroup.position.copy(position);
      markerGroup.lookAt(position.clone().add(normal));
      // Store location data with the marker group
      markerGroup.userData = {
        locationId: id,
        isSelected: id === selectedLocationId,
        marker: marker,
        glow: glow
      };
      // Add marker to the markers group
      markersGroup.add(markerGroup);
      markersRef.current.push(markerGroup);
    });
  };
  // Main initialization effect
  useEffect(() => {
    // Check if THREE is available
    if (!THREE) {
      console.error('Three.js is not available');
      setThreeJsError(true);
      setIsLoading(false);
      return;
    }
    try {
      // Only continue if container exists
      if (!containerRef.current) {
        console.error('Container ref is not available');
        setLoadError(true);
        setIsLoading(false);
        return;
      }
      // Scene setup
      const scene = new THREE.Scene();
      sceneRef.current = scene;
      // Get container dimensions
      const containerWidth = containerRef.current.clientWidth || 300;
      const containerHeight = containerRef.current.clientHeight || 300;
      // Camera setup with optimal position
      const camera = new THREE.PerspectiveCamera(45, containerWidth / containerHeight, 0.1, 1000);
      // Set initial camera position to ensure globe is fully visible
      const initialZoom = calculateOptimalCameraPosition(containerWidth, containerHeight);
      camera.position.z = initialZoom;
      cameraRef.current = camera;
      // Renderer setup
      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true
      });
      renderer.setSize(containerWidth, containerHeight);
      renderer.setClearColor(0x000000, 0);
      // Clear any previous children
      while (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild);
      }
      containerRef.current.appendChild(renderer.domElement);
      rendererRef.current = renderer;
      // Verify renderer was added to DOM
      if (!containerRef.current.contains(renderer.domElement)) {
        console.error('Renderer could not be added to container');
        setLoadError(true);
        setIsLoading(false);
        return;
      }
      // Lighting
      const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);
      scene.add(ambientLight);
      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(5, 3, 5);
      scene.add(directionalLight);
      // Add a second light from another angle for more dimension
      const secondaryLight = new THREE.DirectionalLight(0xffffff, 0.4);
      secondaryLight.position.set(-5, -2, 3);
      scene.add(secondaryLight);
      // Create a basic globe immediately to ensure something is visible
      const earthGeometry = new THREE.SphereGeometry(GLOBE_RADIUS, 64, 64);
      const earthMaterial = new THREE.MeshPhongMaterial({
        color: 0x2233ff,
        emissive: 0x112244,
        specular: 0xffffff,
        shininess: 30
      });
      const earthMesh = new THREE.Mesh(earthGeometry, earthMaterial);
      scene.add(earthMesh);
      globeRef.current = earthMesh;
      // Add location markers
      addLocationMarkers(GLOBE_RADIUS);
      // Earth texture (try to load after basic globe is created)
      const textureLoader = new THREE.TextureLoader();
      // Try loading the first Earth texture
      textureLoader.load('https://eoimages.gsfc.nasa.gov/images/imagerecords/73000/73909/world.topo.bathy.200412.3x5400x2700.jpg', texture => {
        // Update the existing globe with texture
        earthMesh.material = new THREE.MeshPhongMaterial({
          map: texture,
          specular: 0x333333,
          shininess: 15,
          bumpScale: 0.05
        });
        // Ensure markers are visible
        addLocationMarkers(GLOBE_RADIUS);
        setIsLoading(false);
      },
      // Progress callback
      undefined,
      // Error callback
      error => {
        console.error('Error loading earth texture:', error);
        // Try a different texture as backup
        textureLoader.load('https://eoimages.gsfc.nasa.gov/images/imagerecords/57000/57752/land_shallow_topo_2048.jpg', backupTexture => {
          earthMesh.material = new THREE.MeshPhongMaterial({
            map: backupTexture,
            specular: 0x333333,
            shininess: 15
          });
          addLocationMarkers(GLOBE_RADIUS);
          setIsLoading(false);
        }, undefined,
        // If both NASA textures fail, try a third option
        () => {
          textureLoader.load('https://raw.githubusercontent.com/mrdoob/three.js/master/examples/textures/planets/earth_atmos_2048.jpg', thirdTexture => {
            earthMesh.material = new THREE.MeshPhongMaterial({
              map: thirdTexture,
              specular: 0x333333,
              shininess: 15
            });
            addLocationMarkers(GLOBE_RADIUS);
            setIsLoading(false);
          }, undefined, () => {
            // If all textures fail, just keep the colored sphere
            setIsLoading(false);
          });
        });
      });
      // Handle window resize
      const handleResize = () => {
        if (!containerRef.current) return;
        const width = containerRef.current.clientWidth || 300;
        const height = containerRef.current.clientHeight || 300;
        // Update camera aspect ratio
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        // Adjust camera position to ensure globe remains fully visible
        camera.position.z = calculateOptimalCameraPosition(width, height);
        // Update renderer size
        renderer.setSize(width, height);
      };
      window.addEventListener('resize', handleResize);
      // Force an initial resize
      handleResize();
      // Mouse event handlers
      const handleMouseDown = e => {
        // Prevent default to ensure no text selection or other browser actions
        e.preventDefault();
        isDraggingRef.current = true;
        previousMousePositionRef.current = {
          x: e.clientX,
          y: e.clientY
        };
      };
      const handleMouseMove = e => {
        if (!isDraggingRef.current || !globeRef.current) return;
        // Prevent default to ensure smooth dragging
        e.preventDefault();
        const deltaMove = {
          x: e.clientX - previousMousePositionRef.current.x,
          y: e.clientY - previousMousePositionRef.current.y
        };
        // Rotate the globe based on mouse movement
        globeRef.current.rotation.y += deltaMove.x * 0.005;
        globeRef.current.rotation.x += deltaMove.y * 0.005;
        previousMousePositionRef.current = {
          x: e.clientX,
          y: e.clientY
        };
      };
      const handleMouseUp = e => {
        e.preventDefault();
        isDraggingRef.current = false;
      };
      // Add scroll event handler for globe rotation
      const handleScroll = e => {
        if (!globeRef.current) return;
        // Prevent default to stop page scrolling
        e.preventDefault();
        e.stopPropagation();
        // Determine rotation direction and speed based on scroll
        const scrollSpeed = 0.005;
        // Rotate horizontally with wheel delta
        // Negative multiplier to make the direction feel natural
        globeRef.current.rotation.y -= e.deltaX * scrollSpeed;
        // Rotate vertically with wheel delta
        // Limit vertical rotation to prevent flipping over
        const newRotationX = globeRef.current.rotation.x - e.deltaY * scrollSpeed;
        const maxRotation = Math.PI / 2 - 0.1; // Slightly less than 90 degrees
        globeRef.current.rotation.x = Math.max(Math.min(newRotationX, maxRotation), -maxRotation);
      };
      // Click handler for location selection
      const handleClick = event => {
        // Prevent default to ensure the click is captured by our handler
        event.preventDefault();
        event.stopPropagation();
        if (!containerRef.current || !THREE) return;
        // Calculate mouse position in normalized device coordinates
        const rect = renderer.domElement.getBoundingClientRect();
        const mouse = new THREE.Vector2();
        mouse.x = (event.clientX - rect.left) / rect.width * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
        // Update the picking ray with the camera and mouse position
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera(mouse, camera);
        // Find all marker objects in the scene
        const allObjects = [];
        scene.traverse(object => {
          if (object.userData && object.userData.locationId !== undefined) {
            allObjects.push(object);
          }
        });
        // Calculate objects intersecting the picking ray
        const intersects = raycaster.intersectObjects(allObjects, true);
        if (intersects.length > 0) {
          // Find the parent group that contains the location data
          let targetObject = intersects[0].object;
          while (targetObject && targetObject.userData && targetObject.userData.locationId === undefined) {
            targetObject = targetObject.parent;
          }
          if (targetObject && targetObject.userData && targetObject.userData.locationId !== undefined) {
            console.log('Location selected:', targetObject.userData.locationId);
            onSelectLocation(targetObject.userData.locationId);
          }
        }
      };
      // Add event listeners with the capture phase to ensure they get first priority
      renderer.domElement.addEventListener('mousedown', handleMouseDown, {
        capture: true
      });
      renderer.domElement.addEventListener('mousemove', handleMouseMove, {
        capture: true
      });
      renderer.domElement.addEventListener('mouseup', handleMouseUp, {
        capture: true
      });
      renderer.domElement.addEventListener('mouseleave', handleMouseUp, {
        capture: true
      });
      renderer.domElement.addEventListener('wheel', handleScroll, {
        passive: false,
        capture: true
      });
      renderer.domElement.addEventListener('click', handleClick, {
        capture: true
      });
      // Make sure the canvas can receive focus for keyboard events
      renderer.domElement.tabIndex = 0;
      renderer.domElement.style.outline = 'none';
      // Animation loop
      const animate = () => {
        animationFrameIdRef.current = requestAnimationFrame(animate);
        // No auto-rotation - only render the scene
        renderer.render(scene, camera);
      };
      // Start animation
      animate();
      // Cleanup function
      return () => {
        window.removeEventListener('resize', handleResize);
        if (renderer.domElement) {
          renderer.domElement.removeEventListener('mousedown', handleMouseDown, {
            capture: true
          });
          renderer.domElement.removeEventListener('mousemove', handleMouseMove, {
            capture: true
          });
          renderer.domElement.removeEventListener('mouseup', handleMouseUp, {
            capture: true
          });
          renderer.domElement.removeEventListener('mouseleave', handleMouseUp, {
            capture: true
          });
          renderer.domElement.removeEventListener('wheel', handleScroll, {
            capture: true
          });
          renderer.domElement.removeEventListener('click', handleClick, {
            capture: true
          });
        }
        if (containerRef.current && renderer.domElement) {
          containerRef.current.removeChild(renderer.domElement);
        }
        // Cancel animation frame
        if (animationFrameIdRef.current) {
          cancelAnimationFrame(animationFrameIdRef.current);
        }
        // Dispose of Three.js resources
        if (globeRef.current) {
          if (globeRef.current.geometry) globeRef.current.geometry.dispose();
          if (globeRef.current.material) {
            if (Array.isArray(globeRef.current.material)) {
              globeRef.current.material.forEach(material => material.dispose());
            } else {
              globeRef.current.material.dispose();
            }
          }
        }
        markersRef.current.forEach(marker => {
          if (marker.geometry) marker.geometry.dispose();
          if (marker.material) {
            if (Array.isArray(marker.material)) {
              marker.material.forEach(material => material.dispose());
            } else {
              marker.material.dispose();
            }
          }
        });
        // Dispose of renderer
        if (rendererRef.current) {
          rendererRef.current.dispose();
        }
      };
    } catch (error) {
      console.error('Error initializing Three.js:', error);
      setLoadError(true);
      setIsLoading(false);
    }
  }, []);
  // Update markers when selected location changes
  useEffect(() => {
    if (!THREE || markersRef.current.length === 0) return;
    markersRef.current.forEach(markerGroup => {
      const isSelected = markerGroup.userData.locationId === selectedLocationId;
      markerGroup.userData.isSelected = isSelected;
      if (isSelected) {
        // Enhance the selected marker
        markerGroup.userData.marker.material.opacity = 1;
        markerGroup.userData.glow.material.opacity = 0.6;
        markerGroup.scale.set(1.5, 1.5, 1.5);
        // Make the selected marker brighter
        markerGroup.userData.marker.material.color.set(0x3dff84); // Brighter green
      } else {
        // Standard appearance for non-selected markers
        markerGroup.userData.marker.material.opacity = 0.9;
        markerGroup.userData.glow.material.opacity = 0.3;
        markerGroup.scale.set(1, 1, 1);
        markerGroup.userData.marker.material.color.set(0x1db954); // Regular Spotify green
      }
    });
  }, [selectedLocationId]);
  // Zoom controls
  const handleZoomIn = e => {
    e.preventDefault();
    e.stopPropagation();
    if (cameraRef.current) {
      cameraRef.current.position.z = Math.max(MIN_ZOOM, cameraRef.current.position.z - 0.5);
    }
  };
  const handleZoomOut = e => {
    e.preventDefault();
    e.stopPropagation();
    if (cameraRef.current) {
      cameraRef.current.position.z = Math.min(MAX_ZOOM, cameraRef.current.position.z + 0.5);
    }
  };
  const handleReset = e => {
    e.preventDefault();
    e.stopPropagation();
    if (cameraRef.current && globeRef.current) {
      cameraRef.current.position.z = DEFAULT_ZOOM; // Reset to default zoom level
      globeRef.current.rotation.x = 0;
      globeRef.current.rotation.y = 0;
    }
  };
  return <div className="relative w-full h-full bg-[#121212] rounded-lg overflow-hidden" data-id="element-12">
      {isLoading && <div className="absolute inset-0 flex items-center justify-center" data-id="element-13">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1DB954]" data-id="element-14"></div>
        </div>}
      {loadError && <div className="absolute inset-0 flex items-center justify-center" data-id="element-15">
          <div className="text-center text-red-500 p-4" data-id="element-16">
            <p className="text-lg font-bold mb-2" data-id="element-17">Error loading globe</p>
            <p data-id="element-18">Please check your internet connection and try again.</p>
          </div>
        </div>}
      {threeJsError && <div className="absolute inset-0 flex items-center justify-center" data-id="element-19">
          <div className="text-center text-red-500 p-4" data-id="element-20">
            <p className="text-lg font-bold mb-2" data-id="element-21">Three.js not available</p>
            <p data-id="element-22">Please make sure Three.js is installed in the project.</p>
          </div>
        </div>}
      <div ref={containerRef} className="w-full h-full" style={{
      position: 'relative',
      minHeight: '300px',
      pointerEvents: 'all',
      zIndex: 20
    }} data-id="element-23" />
      <div className="absolute bottom-4 right-4 flex flex-col space-y-2" style={{
      zIndex: 30
    }} data-id="element-24">
        <button onClick={handleZoomIn} className="bg-[#282828] p-2 rounded-full hover:bg-[#3E3E3E] transition-colors" aria-label="Zoom in" data-id="element-25">
          <ZoomInIcon size={20} className="text-white" data-id="element-26" />
        </button>
        <button onClick={handleZoomOut} className="bg-[#282828] p-2 rounded-full hover:bg-[#3E3E3E] transition-colors" aria-label="Zoom out" data-id="element-27">
          <ZoomOutIcon size={20} className="text-white" data-id="element-28" />
        </button>
        <button onClick={handleReset} className="bg-[#282828] p-2 rounded-full hover:bg-[#3E3E3E] transition-colors" aria-label="Reset view" data-id="element-29">
          <RefreshCwIcon size={20} className="text-white" data-id="element-30" />
        </button>
      </div>
      <div className="absolute top-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm" style={{
      zIndex: 30
    }} data-id="element-31">
        Scroll to rotate the globe
      </div>
    </div>;
}